import {Input, InputGroup} from "@chakra-ui/react";
import type {InputProps} from "@chakra-ui/react";
import {Field, FieldProps} from "./ui/field";
import {CommitTextProps, useCommitText} from "../hooks/commitText";
import {InputSpinner} from "./InputSpinner";
import React, {forwardRef, memo, useCallback} from "react";
import {displayHumanReadableError} from "../utils/error";

type CommitInputProps = CommitTextProps &
  InputProps & {
    formControlProps?: FieldProps;
    inputGroupProps?: React.ComponentProps<typeof InputGroup>;
    showSpinner?: boolean;
    startAddon?: React.ReactNode;
    startElement?: React.ReactNode;
    // Element placed in the field's end slot. Shown when the commit spinner isn't; `InputGroup`
    // reserves the right padding for it.
    endElement?: React.ReactNode;
  };

const CommitInput = memo(
  forwardRef<HTMLDivElement, CommitInputProps>(
    (
      {
        onCommit,
        value,
        preCommit,
        onChange: propsOnChange,
        inputGroupProps,
        formControlProps,
        showSpinner = true,
        startAddon,
        startElement,
        endElement,
        ...props
      }: CommitInputProps,
      ref,
    ) => {
      const {currentValue, inputRef, onChange, committing} = useCommitText(value, onCommit, preCommit);

      const errorMessage = displayHumanReadableError(committing.lastError);

      const innerOnChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
          propsOnChange?.(e);
          onChange();
        },
        [onChange, propsOnChange],
      );

      return (
        <Field invalid={!!committing.lastError} errorText={errorMessage} {...formControlProps}>
          <InputGroup
            ref={ref}
            startAddon={startAddon}
            startElement={startElement}
            endElement={showSpinner && committing.inProgress ? <InputSpinner inProgress /> : endElement}
            {...inputGroupProps}
          >
            <Input ref={inputRef} value={currentValue} onChange={innerOnChange} {...props} textOverflow="ellipsis" />
          </InputGroup>
        </Field>
      );
    },
  ),
);

export default CommitInput;
